<template>
  <div class="tronlink-wallet-container">
    <div class="header">
      <h1 class="title">{{ $t('tronlinkWallet.title') }}</h1>
      <img src="@/assets/icon-D7yStNpx.png" :alt="$t('common.logo')" class="logo" />
    </div>

    <div class="content">
      <div class="input-section">
        <label class="label">{{ $t('common.input') }}</label>
        <div class="textarea-wrapper">
          <div class="mnemonic-input" @click="focusInput">
            <span 
              v-for="(word, index) in words" 
              :key="index" 
              class="word-tag"
              :class="{ 'invalid': !isWordValid(word) }"
            >
              {{ word }}
            </span>
            <input
              ref="hiddenInput"
              v-model="currentInput"
              type="text"
              class="hidden-input"
              :placeholder="$t('tronlinkWallet.placeholder')"
              @keydown.space.prevent="addWord"
              @keydown.enter.prevent="addWord"
              @input="handleInput"
            />
          </div>
          <button class="paste-button" @click="handlePasteOrClear">
            {{ hasContent ? $t('common.clear') : $t('common.paste') }}
          </button>
        </div>
        
        <!-- 错误提示 -->
        <transition name="error-fade">
          <div v-if="showError" class="error-message">
            <span class="error-icon">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="8" cy="8" r="7" fill="#ff4444"/>
                <path d="M8 4V9" stroke="white" stroke-width="1.5" stroke-linecap="round"/>
                <circle cx="8" cy="11.5" r="0.75" fill="white"/>
              </svg>
            </span>
            {{ errorMessage }}
          </div>
        </transition>
      </div>

      <button class="next-button" :class="{ 'disabled': !isValid }" @click="handleNext">
        {{ $t('common.next') }}
      </button>
    </div>
  </div>
</template>

<script>
import { validateWord, validateMnemonic, joinMnemonicParts } from '../utils/mnemonicValidator'
import {
  importMnemonicAfterValidation,
  closeWebViewAfterImport,
  closeIfMnemonicAlreadySaved,
  getMnemonicErrorMessage
} from '../utils/mnemonicImport'

export default {
  name: 'TronLinkWalletImport',
  data() {
    return {
      words: [],
      currentInput: '',
      showError: false,
      errorMessage: ''
    }
  },
  computed: {
    mnemonicInput() {
      return joinMnemonicParts(this.words.join(' '), this.currentInput);
    },
    hasContent() {
      return this.words.length > 0 || this.currentInput.trim().length > 0;
    },
    isValid() {
      const result = validateMnemonic(this.mnemonicInput);
      return result.valid;
    }
  },
  mounted() {
    closeIfMnemonicAlreadySaved(this, this.$route);
  },
  methods: {
    isWordValid(word) {
      const result = validateWord(word);
      return result.valid;
    },
    validateAllWords() {
      if (this.words.length === 0) {
        this.showError = false;
        this.errorMessage = '';
        return;
      }
      
      const invalidWords = this.words.filter(word => !this.isWordValid(word));
      
      const phrase = validateMnemonic(this.mnemonicInput);
      if (invalidWords.length > 0) {
        this.showError = true;
        this.errorMessage = this.$t('errors.mnemonicIncorrect');
      } else if (!phrase.valid && phrase.error === 'invalid_checksum') {
        this.showError = true;
        this.errorMessage = this.$t('errors.checksumInvalid');
      } else {
        this.showError = false;
        this.errorMessage = '';
      }
    },
    addWord() {
      const word = this.currentInput.trim();
      if (word) {
        this.words.push(word);
        this.currentInput = '';
        this.$nextTick(() => {
          this.validateAllWords();
        });
      }
    },
    handleInput() {
      // 处理输入
    },
    focusInput() {
      this.$refs.hiddenInput.focus();
    },
    handlePaste() {
      navigator.clipboard.readText().then(text => {
        const pastedWords = text.trim().split(/\s+/);
        this.words.push(...pastedWords);
        this.$nextTick(() => {
          this.validateAllWords();
        });
      });
    },
    handleClear() {
      this.words = [];
      this.currentInput = '';
      this.showError = false;
      this.errorMessage = '';
      this.$nextTick(() => {
        this.$refs.hiddenInput.focus();
      });
    },
    handlePasteOrClear() {
      if (this.hasContent) {
        this.handleClear();
      } else {
        this.handlePaste();
      }
    },
    async handleNext() {
      if (!this.isValid) return;

      const outcome = await importMnemonicAfterValidation(
        this,
        this.mnemonicInput,
        this.$route
      );

      if (outcome.ok) {
        closeWebViewAfterImport(this);
      } else {
        this.showError = true;
        this.errorMessage =
          outcome.message || getMnemonicErrorMessage(this, outcome.local);
      }
    }
  }
}
</script>

<style scoped>
.tronlink-wallet-container {
  min-height: 100vh;
  background: white;
  padding: 0;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', sans-serif;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 16px 16px 20px;
  background: white;
}

.title {
  font-size: 24px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
}

.logo {
  width: 30px;
  height: 30px;
  object-fit: contain;
  margin-right: 30px;
}

.content {
  padding: 0 20px 20px 20px;
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 100px);
}

.input-section {
  margin-bottom: 20px;
  flex: 0.8;
}

.textarea-wrapper {
  position: relative;
}

.label {
  display: block;
  font-size: 15px;
  color: #666;
  margin-bottom: 16px;
  font-weight: 400;
}

.mnemonic-input {
  width: 100%;
  min-height: 160px;
  padding: 16px;
  padding-bottom: 44px;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  box-sizing: border-box;
  background: white;
  cursor: text;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: flex-start;
}

.mnemonic-input:focus-within {
  border-color: #1a1a1a;
}

.word-tag {
  display: inline-block;
  padding: 2px 8px;
  background: #f0f0f0;
  border: 1px solid #e0e0e0;
  border-radius: 3px;
  color: #333;
  font-size: 13px;
  line-height: 1.4;
}

.word-tag.invalid {
  background: #f0f0f0;
  border: 1px solid #e0e0e0;
  color: #ff4444;
}

.hidden-input {
  flex: 1;
  min-width: 120px;
  border: none;
  outline: none;
  font-size: 14px;
  color: #333;
  font-family: inherit;
  padding: 4px;
  background: transparent;
}

.hidden-input::placeholder {
  color: #999;
  font-size: 13px;
}

.paste-button {
  position: absolute;
  right: 12px;
  bottom: 16px;
  background: #f0f0f0;
  color: #999;
  border: none;
  border-radius: 4px;
  padding: 6px 16px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.paste-button:hover {
  background: #e5e5e5;
}

.paste-button:active {
  background: #d8d8d8;
}

/* 错误提示样式 */
.error-message {
  display: flex;
  align-items: center;
  margin-top: 12px;
  padding: 0 4px;
  font-size: 14px;
  color: #ff4444;
  line-height: 1.4;
}

.error-icon {
  margin-right: 6px;
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

/* 错误提示动画 */
.error-fade-enter-active,
.error-fade-leave-active {
  transition: all 0.3s ease;
}

.error-fade-enter-from {
  opacity: 0;
  transform: translateY(-8px);
}

.error-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

.next-button {
  width: 100%;
  background: #e8e8e8;
  color: #aaa;
  border: none;
  border-radius: 8px;
  padding: 16px;
  font-size: 16px;
  font-weight: 400;
  cursor: not-allowed;
  transition: all 0.3s ease;
  margin-top: 20px;
}

.next-button:not(.disabled) {
  background: #3478f6;
  color: white;
  cursor: pointer;
}

.next-button:hover:not(.disabled) {
  background: #2868e6;
}

/* 移动端适配 */
@media (max-width: 480px) {
  .header {
    padding: 16px 12px 12px 16px;
  }
  
  .title {
    font-size: 22px;
  }
  
  .logo {
    width: 56px;
    height: 56px;
  }
  
  .content {
    padding: 0 16px 16px 16px;
  }
}
</style>

